import cn from "classnames"
import React, { useState, useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import styles from "./sign_preview.module.css"
import SignSVGInline from "@/common/components/sign_svg_inline"
import { sizeUnit } from "@/common/locales"
import { isMobile } from "@/common/util"
import Calc from "@/common/util/calculator"
import handComparison from "@/images/layout/preview-hand-comparison.png"
import humanComparison from "@/images/layout/preview-human-comparison.png"
import roomLarge from "@/images/layout/preview-room-large.png"
import roomSmall from "@/images/layout/preview-room-small.png"
import { CloseLarge, ChevronLeftLarge, ChevronRightLarge } from "@/skylticons"

type SignPosition = "human" | "room" | "center"

const reducedHeight = (signWidth: number, signHeight: number, signPosition: SignPosition) => {
  const roomHeightMm = signWidth > 340 || signHeight > 340 ? 3022 : 1278
  const humanHeightMm = signWidth > 340 || signHeight > 340 ? 1800 : 326
  const comparisonHeight = signPosition === "room" ? roomHeightMm : humanHeightMm

  return ((signHeight || 0) / comparisonHeight) * 100 > (isMobile() ? 40 : 70) &&
    (signPosition || signPosition) != "room"
    ? 40
    : 100
}

const defaultPreviewArray = (
  svg: string[] | string,
  signWidthMm: number,
  signHeightMm: number,
  singleView: boolean
) => {
  const previewArray =
    signWidthMm > 340 || signHeightMm > 340
      ? svg.length === 2 && !singleView
        ? [[svg[0]], roomLarge, humanComparison, [svg[1]], roomLarge, humanComparison]
        : [svg, roomLarge, humanComparison]
      : svg.length === 2 && !singleView
        ? [[svg[0]], roomSmall, handComparison, [svg[1]], roomSmall, handComparison]
        : [svg, roomSmall, handComparison]

  if (singleView && svg.length === 2) {
    previewArray.splice(0, 3, [svg[0]])
    previewArray.splice(1, 0, [svg[1]])
  } else if (singleView || signWidthMm > 1040 || signHeightMm > 1600) {
    previewArray.splice(0, 3, svg)
  }

  return previewArray
}

interface SlideData {
  roomSlides: number[]
  signPosition: SignPosition
  showSmallSvg: boolean
  previewImg?: "room-large" | "room-small"
  sideA: boolean
}

const currentSlideData = (
  extraImagesCount: number,
  signWidthMm: number,
  signHeightMm: number,
  slide: number,
  shoppingImageCount: number,
  doubleSided: boolean
): SlideData => {
  const fullSvgSlides = doubleSided ? [shoppingImageCount, shoppingImageCount + 1] : [shoppingImageCount]
  const fullSvgCount = fullSvgSlides.length

  const imagesBeforeComparison = shoppingImageCount + fullSvgCount + extraImagesCount

  const roomSlides = [imagesBeforeComparison, imagesBeforeComparison + 2]
  const humanSlides = [imagesBeforeComparison + 1, imagesBeforeComparison + 3]

  let signPosition: SignPosition = "center"
  if (humanSlides.includes(slide)) {
    signPosition = "human"
  } else if (roomSlides.includes(slide)) {
    signPosition = "room"
  }
  const showSmallSvg = ["human", "room"].includes(signPosition)
  const previewImg = showSmallSvg ? (signWidthMm > 340 || signHeightMm > 340 ? "room-large" : "room-small") : undefined

  return {
    roomSlides: roomSlides,
    signPosition: signPosition,
    showSmallSvg: showSmallSvg,
    previewImg: previewImg,
    sideA: [imagesBeforeComparison, imagesBeforeComparison + 1].includes(slide) || slide == shoppingImageCount,
  }
}

const defaultArrayCurrentSlideData = (signWidthMm: number, signHeightMm: number, slide: number): SlideData => {
  const roomSlides = [1, 4]
  const humanSlides = [2, 5]

  let signPosition: SignPosition = "center"
  if (humanSlides.includes(slide)) {
    signPosition = "human"
  } else if (roomSlides.includes(slide)) {
    signPosition = "room"
  }
  const showSmallSvg = ["human", "room"].includes(signPosition)
  const previewImg = showSmallSvg ? (signWidthMm > 340 || signHeightMm > 340 ? "room-large" : "room-small") : undefined

  return {
    roomSlides: roomSlides,
    signPosition: signPosition,
    showSmallSvg: showSmallSvg,
    previewImg: previewImg,
    sideA: [0, 1, 2].includes(slide),
  }
}

const imagesOnlySlideData = (): SlideData => {
  return {
    roomSlides: [],
    signPosition: "center",
    showSmallSvg: false,
    previewImg: undefined,
    sideA: false,
  }
}

interface PreviewContentProps {
  svg?: string[] | string
  signWidthMm: number
  signHeightMm: number
  singleView?: boolean
  customArray?: any[]
  extraImagesCount?: number
  shoppingImageCount?: number
  initSlide?: number
  transparent?: boolean
  title?: string
  subTitle?: string
  close: () => void
}

const PreviewContent: React.FC<PreviewContentProps> = ({
  svg,
  signWidthMm,
  signHeightMm,
  singleView,
  customArray,
  extraImagesCount,
  shoppingImageCount,
  initSlide,
  transparent,
  title,
  subTitle,
  close,
}) => {
  const { t } = useTranslation()
  const previewArray = customArray || defaultPreviewArray(svg || [], signWidthMm, signHeightMm, singleView || false)

  const slides = previewArray.length
  const [slide, setSlide] = useState(initSlide || 0)
  const slideBtnsWrapper = useRef<HTMLDivElement>(null)

  let slideData: SlideData
  if (!svg) {
    slideData = imagesOnlySlideData()
  } else if (customArray) {
    slideData = currentSlideData(
      extraImagesCount || 0,
      signWidthMm,
      signHeightMm,
      slide || 0,
      shoppingImageCount || 0,
      svg?.length == 2
    )
  } else {
    slideData = defaultArrayCurrentSlideData(signWidthMm, signHeightMm, slide || 0)
  }

  const changeSlide = useCallback(
    (newSlide: number) => {
      let adapted
      if (newSlide < 0) {
        adapted = slides - 1
      } else if (newSlide === slides) {
        adapted = 0
      } else {
        adapted = newSlide
      }

      setSlide(adapted)

      const slideBtn = slideBtnsWrapper.current?.children[adapted]
      if (slideBtn) {
        slideBtn.scrollIntoView({ behavior: "smooth" })
      }
    },
    [setSlide, slides]
  )

  const comparison = [roomLarge, roomSmall, handComparison, humanComparison].includes(previewArray[slide])

  if (comparison) {
    title = t("preview.size_comparison")

    switch (previewArray[slide]) {
      case roomLarge:
      case roomSmall:
        subTitle = t("preview.comparison_room")
        break
      case handComparison:
        subTitle = t("preview.comparison_hand")
        break
      case humanComparison:
        subTitle = t("preview.comparison_human")
        break
    }
  }

  return (
    <div
      className={cn(
        "z-40",
        styles["signomatic-modal"],
        styles["preview-modal"],
        "signomatic-modal",
        "preview-modal",
        transparent ? styles["transparent"] : ""
      )}
    >
      <div className={styles["preview-background"]} onClick={close} />
      <div className={styles["preview"]}>
        <div className={styles["top-bar"]}>
          <div className={cn(styles["preview-info"], "truncate")}>
            <span className="truncate text-base font-semibold text-black">{title}</span>
            <span className="text-dark truncate text-sm">{subTitle}</span>
          </div>
          <button
            className={cn(
              "button button-subtle preview-close mb-0 inline-flex h-10 flex-shrink-0 items-center justify-center rounded px-4 hover:bg-secondary-light",
              styles["preview-close"]
            )}
            type="button"
            onClick={close}
          >
            <CloseLarge className="h-6 w-6" />
            <p className="mb-0 text-base font-semibold text-black">{t("misc.close")}</p>
          </button>
        </div>
        {previewArray && (
          <div className={styles["preview-main"]}>
            <div className={styles["preview-slideshow"]}>
              {slides > 1 && (
                <div className={cn("z-10", styles["slideshow-buttons"])}>
                  <div
                    className={cn(styles["slide-left"], "button button-large button-secondary flat cursor-pointer")}
                    onClick={() => changeSlide(slide - 1)}
                  >
                    <ChevronLeftLarge />
                  </div>
                  <div
                    className={cn(styles["slide-right"], "button button-large button-secondary flat cursor-pointer")}
                    onClick={() => changeSlide(slide + 1)}
                  >
                    <ChevronRightLarge />
                  </div>
                </div>
              )}
              <div
                className={styles["slideshow-contents"]}
                style={{
                  height: slideData.showSmallSvg
                    ? `${reducedHeight(signWidthMm, signHeightMm, slideData.signPosition)}%`
                    : "100%",
                }}
              >
                <div
                  className={styles["slide"]}
                  style={{ width: `${slides * 100}%`, height: "100%", left: `${slide * -100}%` }}
                >
                  <div className={styles["background-wrapper"]}>
                    {previewArray.map((img, i) => {
                      const extraImage = typeof img === "object" && img.png
                      const svgImage = typeof img != "string" && !img.png
                      const comparisonImage = typeof img === "string" && i != 0
                      const shoppingImage = typeof img === "string" && i == 0

                      return (
                        <div className={styles["img-wrapper"]} key={i}>
                          {svgImage && <SvgPreview svg={img[0]} />}
                          {extraImage && !singleView && (
                            <picture key={img.id} className="flex h-full w-full items-center justify-center">
                              <source srcSet={img.webp2x} type="image/webp" />
                              <img src={img.png2x} />
                            </picture>
                          )}
                          {shoppingImage && !singleView && <img src={img} />}
                          {comparisonImage && !singleView && (
                            <img
                              id={`comparison-img-${i}`}
                              className={cn("max-w-none", {
                                "ml-[160px] md:ml-[400px]": slideData.signPosition == "human",
                              })}
                              src={img}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
                {!singleView && svg && (
                  <div className={cn(styles["svg-wrapper"], styles["svg-with-background"])}>
                    {slideData.showSmallSvg && (
                      <SvgPreview
                        svg={slideData.sideA ? svg[0] : svg[1]}
                        signWidth={signWidthMm}
                        signHeight={signHeightMm}
                        signPosition={slideData.signPosition}
                        previewImg={slideData.previewImg}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles["bottom-bar"]}>
              {slides > 1 && (
                <>
                  <div
                    className={cn(styles["slide-left"], "button button-large button-secondary flat cursor-pointer")}
                    onClick={() => changeSlide(slide - 1)}
                  >
                    <ChevronLeftLarge />
                  </div>
                  <div className={cn(styles["thumbnail-wrapper"], "m-auto")} ref={slideBtnsWrapper}>
                    {previewArray.map((img, i) => {
                      const extraImage = typeof img === "object" && img.png
                      const svgImage = typeof img != "string" && !img.png
                      const comparisonImage = typeof img === "string"

                      return (
                        <button
                          key={i}
                          id={`slide-btn-${i}`}
                          className={cn(
                            "scroll-mx-6 scroll-px-6",
                            styles["thumbnail"],
                            i === slide ? styles["active"] : "",
                            slideData.roomSlides.includes(i) ? styles["room"] : "",
                            extraImage ? styles["extra-image"] : ""
                          )}
                          type="button"
                          onClick={e => {
                            e.preventDefault(), changeSlide(i)
                          }}
                        >
                          {svgImage && <SignSVGInline svg={img[0]} className={(styles["svg"], "data-hj-suppress")} />}
                          {extraImage && <img src={img.png} />}
                          {comparisonImage && <img src={img} alt="" />}
                        </button>
                      )
                    })}
                  </div>
                  <div
                    className={cn(styles["slide-right"], "button button-large button-secondary flat cursor-pointer")}
                    onClick={() => changeSlide(slide + 1)}
                  >
                    <ChevronRightLarge />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface SvgPreviewProps {
  svg: string
  signWidth?: number
  signHeight?: number
  signPosition?: SignPosition
  previewImg?: string | "room-large" | "room-small"
}

const SvgPreview: React.FC<SvgPreviewProps> = ({ svg, signWidth, signHeight, signPosition, previewImg }) => {
  if (signWidth && signHeight && signPosition && previewImg) {
    // calculating sign height based on background height
    const backgroundMargin = window.innerWidth > 767 ? 192 : 120
    const backgroundHeightPx = Math.min(1000, window.innerHeight - backgroundMargin)
    const roomHeightMm = signWidth > 340 || signHeight > 340 ? 3022 : 1278
    const humanHeightMm = signWidth > 340 || signHeight > 340 ? 1800 : 326
    const comparisonHeight = signPosition === "room" ? roomHeightMm : humanHeightMm
    const signHeightPrc = `${((signHeight || 0) / comparisonHeight) * 100}%`

    return (
      <span className={"w-full"} style={{ height: backgroundHeightPx }}>
        <div
          className="w-full"
          style={{
            height: `${reducedHeight(signWidth, signHeight, signPosition)}%`,
          }}
        >
          <SignSVGInline
            component="div"
            svg={svg}
            height={signHeightPrc}
            className={cn("preview", styles["svg"], styles[signPosition], styles[previewImg], "data-hj-suppress")}
          />
        </div>
      </span>
    )
  }

  return <SignSVGInline component="div" svg={svg} className={cn(styles["svg"], "data-hj-suppress")} />
}

export interface SignPreviewOptions {
  svg?: string[] | string
  transparent?: boolean
  withBackground?: boolean
  signWidth?: number
  signHeight?: number
  signType?: string
  metaProduct?: string
  fixMethod?: string
  singleView?: boolean
  orderLocale?: string
}

export const useSignPreview = (): [(options: SignPreviewOptions) => void, SignPreviewProps, () => void] => {
  const [modalProps, setModalProps] = useState<SignPreviewProps>({ close: () => null, open: false })

  const close = useCallback(() => {
    setModalProps({ ...modalProps, open: false, close })
  }, [modalProps, setModalProps])

  const open = useCallback(
    (options: SignPreviewOptions) => {
      setModalProps({ ...options, open: true, close })
    },
    [setModalProps, close]
  )

  return [open, modalProps, close]
}

interface SignPreviewMethods {
  open: (options: SignPreviewOptions) => void
  modalProps: SignPreviewProps
  close: () => void
}

const SignPreviewContext = React.createContext<SignPreviewMethods | null>(null)
SignPreviewContext.displayName = "SignPreviewContext"

export const useSignPreviewContext = () => React.useContext(SignPreviewContext) as SignPreviewMethods

export const SignPreviewContextProvider = ({
  children,
  ...props
}: SignPreviewMethods & {
  children: React.ReactNode
}) => <SignPreviewContext.Provider value={{ ...props } as SignPreviewMethods}>{children}</SignPreviewContext.Provider>

interface SignPreviewProps extends SignPreviewOptions {
  open: boolean
  close: () => void
}

const SignPreview: React.FC<SignPreviewProps> = ({
  svg,
  transparent,
  withBackground,
  signWidth,
  signHeight,
  signType,
  open,
  close,
  metaProduct,
  fixMethod,
  singleView,
  orderLocale,
}) => {
  const { t } = useTranslation()
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close()
      }
    },
    [close]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, { capture: true })

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true })
    }
  }, [handleKeyDown])

  if (!svg) {
    return <React.Fragment />
  }

  const root = document.body
  const signDimensions = `${signWidth}x${signHeight} ${sizeUnit}`
  const unitInches = sizeUnit === "in" || orderLocale === "en-US"

  if (typeof svg === "string") {
    svg = [svg]
  }

  const signs = svg ? svg.filter(s => s) : null

  if (!open || !signs) {
    return null
  }

  const title = t(`sign_type.${metaProduct ? metaProduct : signType}`)
  let subTitle = signDimensions

  if (fixMethod) {
    subTitle += `, ${t(`hang_type.${fixMethod}`)}`
  }

  if (withBackground && signs) {
    return createPortal(
      <PreviewContent
        svg={signs}
        signWidthMm={unitInches ? Calc.inToMm(signWidth || 0) : signWidth || 0}
        signHeightMm={unitInches ? Calc.inToMm(signHeight || 0) : signHeight || 0}
        singleView={singleView}
        transparent={transparent}
        title={title}
        subTitle={subTitle}
        close={close}
      />,

      root
    )
  }

  return createPortal(
    <div
      className={cn(
        styles["signomatic-modal"],
        styles["preview-modal"],
        "signomatic-modal",
        "preview-modal",
        "admin-preview",
        transparent ? styles["transparent"] : ""
      )}
      onClick={close}
    >
      <div className={styles["preview-background"]} />
      {signs && (
        <div
          className={cn(
            styles["svg-wrapper"],
            styles["svg-without-background"],
            signs.length > 1 ? styles["double-sided"] : ""
          )}
        >
          {signs.map((svg, i) => (
            <SvgPreview key={i} svg={svg} />
          ))}
        </div>
      )}
    </div>,
    root
  )
}

export default SignPreview
